/**
 * POS totals mirroring App\Support\MoneyMath + SaleFinancialCalculator.
 *
 * The sale chain is computed at the sale's precision — two places or four, the
 * `sale_decimal_places` setting — natively, one rounding per step: integer paisa
 * at two (what the chain always did) and integer ten-thousandths (RATE_SCALE) at
 * four, where a tax-inclusive Rs 100.00 shelf price totals 100.00 rather than
 * 100.01. Only the payable is settled to the paisa, once. Percentages are basis
 * points (18% → 1800). Floats come from fromScaled / fromCents so they match PHP
 * round-half-up. See docs/pos-four-decimal-precision-plan.md.
 */

export const normalizeNonNegativeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(number, 0) : 0;
};

/** Round half-up to nearest integer (matches MoneyMath::roundHalfUp). */
export const roundHalfUp = (value) => Math.floor(Number(value) + 0.5);

/**
 * Scale by 100 and round half-up (mirrors MoneyMath::scaleHalfUp).
 *
 * The nudge to 4dp first is what toQuantityUnits already does: 1.005 is held
 * as 1.00499999999999989, so 1.005 * 100 lands just under the half and a bare
 * half-up rounds it down while 10.005 rounds up. Money must not disagree with
 * quantity — or with the server — about which way a half goes.
 */
const scaleHalfUp = (value) => roundHalfUp(Math.round(Number(value) * 100 * 10000) / 10000);

/**
 * Major-unit amount → integer cents.
 * Bare integers mean whole rupees (`100` → 10000), matching MoneyMath::toCents.
 */
export const toCents = (value) => {
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
        return value * 100;
    }
    return scaleHalfUp(normalizeNonNegativeNumber(value));
};

export const fromCents = (cents) => Math.round(Number(cents) || 0) / 100;

/**
 * Where a *rate* is held: ten-thousandths, four decimal places.
 * Mirrors MoneyMath::RATE_SCALE — see the note there for why a rate is not
 * money and does not round to the paisa until it is extended.
 */
export const RATE_SCALE = 10000;

/** Major-unit rate → integer rate units. Mirrors MoneyMath::toRateUnits. */
export const toRateUnits = (value) => {
    const number = normalizeNonNegativeNumber(value);
    return Math.floor(Math.round(number * RATE_SCALE * 10000) / 10000 + 0.5);
};

/** A rate back in major units, at the four places it is held to. */
export const fromRateUnits = (units) => Math.round(Number(units) || 0) / RATE_SCALE;

/** 2 or 4 — the vocabulary the precision settings accept; anything else is 2. */
export const normalizeDecimals = (decimals) => (Number(decimals) === 4 ? 4 : 2);

/** The integer scale a document is computed at: paisa at 2, rate units at 4. Mirrors MoneyMath::scaleFor. */
export const scaleFor = (decimals) => (normalizeDecimals(decimals) === 4 ? RATE_SCALE : 100);

/** A major-unit amount at a document's scale, half-up. Mirrors MoneyMath::toScaled. */
export const toScaled = (value, scale) => {
    const number = normalizeNonNegativeNumber(value);
    return Math.floor(Math.round(number * scale * 10000) / 10000 + 0.5);
};

/** Back from a document's scale to major units. */
export const fromScaled = (units, scale) => Math.round(Number(units) || 0) / scale;

/** A figure at a document's scale expressed in rate units — lossless. */
export const rateUnitsFromScaled = (units, scale) => Math.trunc(Number(units) || 0) * (RATE_SCALE / scale);

/**
 * Extended line total in cents from a 4dp rate and a quantity in hundredths.
 * Mirrors SaleFinancialCalculator::extendedCentsFromRate.
 */
export const extendedCentsFromRate = (unitRateUnits, quantityUnits) => {
    const divisor = (RATE_SCALE * QUANTITY_SCALE) / 100;
    const scaled = Math.max(0, Math.trunc(unitRateUnits)) * Math.max(0, Math.trunc(quantityUnits));
    return Math.trunc(scaled / divisor) + Math.round((scaled % divisor) / divisor);
};

/**
 * An amount as this system shows it: two decimals when that is all it has,
 * four when it genuinely carries more.
 *
 * Money settles in paisa, so almost everything prints at two. A per-unit rate
 * is held to four (MoneyMath::RATE_SCALE), and printing 1,234.5678 as 1,234.57
 * would show the operator a figure their supplier did not write and their own
 * line total was not computed from. Anything past the fourth place is float
 * noise rather than a figure, so it is rounded away before the decision.
 */
export const formatAmount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return (0).toFixed(2);

    // Compared as integers: `x === Math.round(x * 100) / 100` is a float
    // equality test and answers wrongly on values like 0.145.
    const inTenThousandths = Math.round(number * RATE_SCALE);
    const inCents = Math.round(number * 100);

    return inTenThousandths === inCents * 100 ? (inCents / 100).toFixed(2) : (inTenThousandths / RATE_SCALE).toFixed(4);
};

/**
 * A figure as a screen or a receipt shows it, at the display setting's places
 * (2 unless the installation chose 4), rounded half-up on its decimal value
 * exactly as MoneyMath rounds. A figure shown is routinely the rounding of
 * something with more digits — and Number#toFixed rounds the binary double,
 * which holds 100.005 as 100.00499… and prints 100.00 under a payable that
 * settled to 100.01 (it gets about half of all such ties wrong). Signed, unlike
 * roundMoney, because a balance or a difference can be negative.
 */
export const formatMoney = (value, decimals = 2) => {
    const places = normalizeDecimals(decimals);
    const number = Number(value);
    if (!Number.isFinite(number)) return (0).toFixed(places);

    const scale = scaleFor(places);
    const units = toScaled(Math.abs(number), scale);
    return ((number < 0 && units > 0 ? -units : units) / scale).toFixed(places);
};

export const toBasisPoints = (percent) => {
    const number = Math.min(Math.max(normalizeNonNegativeNumber(percent), 0), 100);
    return scaleHalfUp(number);
};

export const percentOf = (amountCents, basisPoints) => {
    const amount = Math.max(0, Math.trunc(amountCents));
    const bps = Math.max(0, Math.trunc(basisPoints));
    return roundHalfUp((amount * bps) / 10000);
};

export const embeddedTaxCents = (lineTotalCents, taxBps) => {
    const total = Math.max(0, Math.trunc(lineTotalCents));
    const bps = Math.max(0, Math.trunc(taxBps));
    if (total === 0 || bps === 0) return 0;
    return roundHalfUp((total * bps) / (10000 + bps));
};

/**
 * The value hiding inside a tax-inclusive amount (mirrors
 * MoneyMath::exclusiveOfTaxCents).
 *
 * Subtracted rather than divided so value + embeddedTaxCents() === total
 * exactly. Used wherever a tax-inclusive figure enters the ex-tax line chain:
 * a third-schedule printed retail price, and the shelf price the POS turns into
 * the rate it quotes.
 */
export const exclusiveOfTaxCents = (inclusiveCents, taxBps) => {
    const total = Math.max(0, Math.trunc(inclusiveCents));
    return total - embeddedTaxCents(total, taxBps);
};

/** Hundredths, matching App\Support\QuantityMath::SCALE and decimal(12,2). */
export const QUANTITY_SCALE = 100;

/**
 * Major-unit quantity → integer hundredths (mirrors QuantityMath::toUnits).
 * A cart line may legitimately be 2.5 kg; flooring it to 2 quotes a total the
 * server will not agree with, and the sale is rejected as tampered.
 */
export const toQuantityUnits = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) return 0;
    // Round to 4dp first so 2.675 * 100 = 267.49999... still lands on 268,
    // exactly as the PHP side does before its half-up step.
    const rounded = Math.round(number * QUANTITY_SCALE * 10000) / 10000;
    return Math.floor(rounded + 0.5);
};

/**
 * Extended line total in cents for a quantity held in hundredths.
 * Mirrors SaleFinancialCalculator::extendedCents.
 */
export const extendedCents = (unitCents, quantityUnits) => {
    const scaled = Math.max(0, Math.trunc(unitCents)) * Math.max(0, Math.trunc(quantityUnits));
    return Math.trunc(scaled / QUANTITY_SCALE) + Math.round((scaled % QUANTITY_SCALE) / QUANTITY_SCALE);
};

/**
 * Exact largest-remainder distribution so SUM(parts) = totalCents.
 * Mirrors App\Support\Apportion::cents.
 *
 * @param {number} totalCents
 * @param {Record<string|number, number>} weights
 * @returns {Record<string|number, number>}
 */
export const apportionCents = (totalCents, weights) => {
    const total = Math.trunc(totalCents);
    if (total < 0) {
        throw new Error('totalCents must be non-negative.');
    }
    const entries = Object.entries(weights ?? {});
    if (entries.length === 0) {
        if (total === 0) return {};
        throw new Error('Cannot apportion a non-zero amount across no parts.');
    }

    let weightSum = 0;
    for (const [, weight] of entries) {
        const w = Math.trunc(weight);
        if (w < 0) throw new Error('Weights must be non-negative.');
        weightSum += w;
    }

    if (weightSum <= 0) {
        if (total === 0) {
            return Object.fromEntries(entries.map(([key]) => [key, 0]));
        }
        throw new Error('Cannot apportion across zero total weight.');
    }

    const floors = {};
    const remainders = {};
    let assigned = 0;
    for (const [key, weight] of entries) {
        const exact = (total * Math.trunc(weight)) / weightSum;
        const floor = Math.floor(exact);
        floors[key] = floor;
        remainders[key] = exact - floor;
        assigned += floor;
    }

    let leftover = total - assigned;
    if (leftover === 0) return floors;

    const order = entries.map(([key]) => key);
    order.sort((a, b) => {
        const cmp = remainders[b] - remainders[a];
        if (cmp !== 0) return cmp;
        return order.indexOf(a) - order.indexOf(b);
    });

    let i = 0;
    const n = order.length;
    while (leftover > 0 && n > 0) {
        floors[order[i % n]]++;
        leftover--;
        i++;
    }

    return floors;
};

/** @deprecated Prefer fromCents(toCents(value)); kept for display-only callers. */
export const roundMoney = (value) => fromCents(toCents(value));

/**
 * A figure rounded to a document's places — a rate, a discount, a line value.
 * The sale's own precision, never a fixed two: at four places a rate rounded to
 * the paisa would bring back the errors the four places exist to avoid.
 */
export const roundTo = (value, decimals) => {
    const scale = scaleFor(decimals);
    return fromScaled(toScaled(value, scale), scale);
};

/** A rate at a document's places (two unless the caller says four). */
export const roundRate = (value, decimals = 2) => roundTo(value, decimals);

/** A figure at a document's scale settled to the paisa — identity at two places. */
export const centsFromScaled = (units, scale) => (scale === 100 ? Math.trunc(Number(units) || 0) : centsFromRateUnits(units));

/**
 * Rate units settled to the paisa, half-up, in integers. Mirrors
 * MoneyMath::centsFromRateUnits — the one place the sale chain becomes money.
 */
export const centsFromRateUnits = (units) => {
    const value = Math.trunc(Number(units) || 0);
    const perCent = RATE_SCALE / 100;
    const magnitude = Math.abs(value);
    const cents = Math.trunc(magnitude / perCent) + ((magnitude % perCent) * 2 >= perCent ? 1 : 0);
    return value < 0 ? -cents : cents;
};

/**
 * The payable (paisa) less the exact total it was settled from (at the
 * document's scale), in rate units: zero at two places, within half a paisa
 * either way at four. Mirrors SaleFinancialCalculator's `rounding_units`.
 */
export const roundingUnitsFromScaled = (payableCents, exactTotalUnits, scale) =>
    Math.trunc(Number(payableCents) || 0) * (RATE_SCALE / 100) - rateUnitsFromScaled(exactTotalUnits, scale);

/**
 * The Rounding row of a document already computed: its payable less the exact
 * total, signed, at four places — in integers, never a float subtraction.
 *
 * `exactTotal` is that total or the parts it is the sum of (net, each header
 * tax, the charges, advance tax), as the surface showing them holds them.
 * `decimals` is the places the document was computed at, and has no default:
 * at two the payable *is* the total, so there is none — and a document written
 * before the precision setting keeps parts from an older convention that need
 * not add up, so a payload that does not say reads as two. A difference beyond
 * half a paisa is not rounding either (the figures in hand are not the whole
 * total), so it is reported as none rather than shown under that name.
 */
/**
 * Paisa settled to the whole rupee, half away from zero: 123450 → 123500 and
 * 123449 → 123400. Mirrors SaleFinancialCalculator::roundCentsToUnit.
 */
export const roundCentsToUnit = (cents) => {
    const whole = Math.trunc(Number(cents) || 0);
    const rounded = Math.floor((Math.abs(whole) + 50) / 100) * 100;

    return rounded === 0 ? 0 : (whole < 0 ? -rounded : rounded);
};

/**
 * The same rounding on a figure held at the document's scale (100 units to the
 * rupee at two places, 10,000 at four): a line's total, settled to the whole
 * rupee half away from zero. Mirrors SaleFinancialCalculator::roundUnitsToUnit.
 */
export const roundUnitsToUnit = (units, scale) => {
    const whole = Math.trunc(Number(units) || 0);
    const step = Math.max(1, Math.trunc(Number(scale) || 100));
    const rounded = Math.floor((Math.abs(whole) + Math.floor(step / 2)) / step) * step;

    return rounded === 0 ? 0 : (whole < 0 ? -rounded : rounded);
};

/**
 * Whether a Rounding row is worth printing: the figure is not nothing at the
 * places the surface shows — so a four-place document's sub-paisa residual
 * shows only on a four-place display, and a whole-rupee rounding shows on any.
 */
export const roundingRowVisible = (rounding, displayDecimals = 2) => {
    const value = Number(rounding) || 0;

    return value !== 0 && Math.abs(value) > 0.5 / 10 ** normalizeDecimals(displayDecimals);
};

export const payableRounding = (payable, exactTotal, decimals) => {
    if (normalizeDecimals(decimals) !== 4) return 0;

    const exactUnits = (Array.isArray(exactTotal) ? exactTotal : [exactTotal]).reduce((sum, part) => sum + toRateUnits(part), 0);
    const units = roundingUnitsFromScaled(toCents(payable), exactUnits, RATE_SCALE);

    return Math.abs(units) * 2 > RATE_SCALE / 100 ? 0 : fromRateUnits(units);
};

/**
 * Extended line value in rate units from a rate in rate units and a quantity in
 * hundredths. Mirrors SaleFinancialCalculator::extendedUnits — the arithmetic of
 * extendedCents() one scale up, so nothing is rounded to the paisa on the way.
 */
export const extendedUnits = (unitRateUnits, quantityUnits) => extendedCents(unitRateUnits, quantityUnits);

/**
 * A tax-inclusive shelf price → the ex-tax rate the cart carries.
 *
 * The catalogue prices goods tax-inclusive (`branch_variants.sales_price`), but
 * every figure in the cart from the rate down is a §2(46) value, so the
 * conversion happens once, when the line is added — at the sale's places. At
 * four, Rs 100.00 at 18% is 84.7458, which totals back to 100.00; at two it is
 * 84.75, which totals 100.01. Returns major units because that is what
 * `item.rate` holds.
 */
export const exclusiveRateFrom = (inclusiveRate, taxPercentage, decimals = 2) => {
    const scale = scaleFor(decimals);
    return fromScaled(exclusiveOfTaxCents(toScaled(inclusiveRate, scale), toBasisPoints(taxPercentage)), scale);
};

/**
 * Canonical POS totals used by both the visible summary and submitted payload.
 * The chain runs at the sale's scale so UI and CreateSaleJob agree to the unit;
 * the payable is settled to the paisa once, at the end.
 *
 * `item.rate` is the unit rate **excluding sales tax**, so the gross and both
 * discounts below are §2(46) figures and the tax is added to what they leave.
 * Line discounts (per item) are applied first; the header discount then applies
 * to what is left after them — matching SaleFinancialCalculator::normalize.
 *
 * The line helpers return integers at the sale's scale (`decimals`, 2 unless
 * the caller says 4) — paisa at two places, ten-thousandths at four.
 */
export const lineGrossUnits = (item, decimals = 2) => {
    const units = toQuantityUnits(item?.qty);
    if (units <= 0) return 0;
    return extendedUnits(toScaled(item?.rate, scaleFor(decimals)), units);
};

export const lineDiscountUnits = (item, decimals = 2, grossUnits = null) => {
    // The gross may be handed in already settled to the rupee (whole-rupee
    // rounding), so a percentage discount stands on the figure the line shows.
    const lineGross = grossUnits ?? lineGrossUnits(item, decimals);
    if (lineGross <= 0) return 0;

    const discountType = item?.discount_type === 'percent' ? 'percent' : 'rs';
    const rawDiscount = normalizeNonNegativeNumber(item?.discount);
    if (discountType === 'percent') {
        return percentOf(lineGross, toBasisPoints(Math.min(rawDiscount, 100)));
    }
    return Math.min(toScaled(rawDiscount, scaleFor(decimals)), lineGross);
};

export const lineDiscountAmount = (item, decimals = 2) => fromScaled(lineDiscountUnits(item, decimals), scaleFor(decimals));

/**
 * The line's own value excluding sales tax, before any share of the invoice
 * discount reaches it. The figure the cart shows as Amount is this less that
 * share, plus the tax — read it off `calculateSaleSummary().lines`, which is the
 * only place the apportionment exists.
 */
export const lineNetAmount = (item, decimals = 2) =>
    fromScaled(Math.max(lineGrossUnits(item, decimals) - lineDiscountUnits(item, decimals), 0), scaleFor(decimals));

/**
 * Display tax % for a cart line. Prefer the stored category/product rate; if
 * missing (older cart rows), recover it from the ex-tax rate and the per-unit
 * tax that was cached alongside it.
 */
export const lineTaxPercentage = (item) => {
    const stored = normalizeNonNegativeNumber(item?.tax_percentage);
    if (stored > 0) return stored;

    const exclusiveUnits = toRateUnits(item?.rate);
    const taxPerUnitUnits = toRateUnits(item?.tax_rate);
    if (exclusiveUnits <= 0 || taxPerUnitUnits <= 0) return 0;

    return roundHalfUp((taxPerUnitUnits * 10000) / exclusiveUnits) / 100;
};

export const calculateSaleSummary = (items = [], form = {}) => {
    // The sale's precision: the setting for a cart, the order's own for a
    // multi-sale. Every chain integer below is at this scale.
    const decimals = normalizeDecimals(form.decimals);
    const scale = scaleFor(decimals);
    const up = (units) => fromScaled(units, scale);
    // Whether the installation rounds invoice totals to the rupee
    // (`form.round_total`, the setting). Mirrors SaleFinancialCalculator: the
    // document is then computed in whole rupees — every extended figure
    // settles to the rupee, half up, as it is made (each line's gross,
    // discounts, tax basis and tax; the header discount; the header taxes,
    // delivery and advance tax), so every figure on the summary is whole,
    // sums of whole rupees are whole, and the payable needs no rounding of
    // its own. Rates stay as typed. Off, `whole` is the identity.
    const roundTotal = Boolean(form.round_total) && !['0', 'false', 'no', 'off'].includes(String(form.round_total).toLowerCase());
    const whole = (units) => (roundTotal ? roundUnitsToUnit(units, scale) : units);

    let grossUnits = 0;
    let rowDiscountUnits = 0;
    const lines = [];
    items.forEach((item, itemIndex) => {
        // Mirror the server: SaleFinancialCalculator drops a line only when it
        // has no quantity, which it rejects outright. A zero-price line is
        // still a supply — a third-schedule freebie is taxed on its printed
        // retail price — so skipping it on gross alone quoted a total without
        // that tax and the checkout was rejected as tampered.
        const quantityUnits = toQuantityUnits(item?.qty);
        if (quantityUnits <= 0) return;
        const lineGross = whole(lineGrossUnits(item, decimals));
        const lineRowDiscount = whole(lineDiscountUnits(item, decimals, lineGross));
        grossUnits += lineGross;
        rowDiscountUnits += lineRowDiscount;
        lines.push({
            // Skipping zero-quantity lines makes this array shorter than the
            // cart, so the cart index travels with the line. Without it the POS
            // read line N's tax onto cart row N and shifted every row below an
            // empty one.
            itemIndex,
            quantityUnits,
            grossUnits: lineGross,
            rowDiscountUnits: lineRowDiscount,
            taxBps: toBasisPoints(lineTaxPercentage(item)),
            isThirdSchedule: Boolean(item?.is_third_schedule ?? item?.third_schedule),
            // Which header taxes the line can bear. Display-side only: the
            // server re-resolves both from the catalogue and ignores the
            // payload, so a tampered cart changes the quote and nothing else.
            supplyType: ['exempt', 'zero_rated'].includes(item?.supply_type) ? item.supply_type : 'standard',
            fedExcisable: Boolean(item?.fed_excisable),
            mrpUnits: toScaled(item?.mrp ?? 0, scale),
        });
    });

    const discountType = form.discount_type === 'percent' ? 'percent' : 'rs';
    const rawDiscount = normalizeNonNegativeNumber(form.discount);
    // Both header discount types work off what is left after the line discounts,
    // so the same 10% means the same thing whichever box the cashier used.
    const discountableUnits = Math.max(grossUnits - rowDiscountUnits, 0);
    const discountValue = discountType === 'percent' ? Math.min(rawDiscount, 100) : Math.min(rawDiscount, up(discountableUnits));

    const headerDiscountUnits = whole(
        discountType === 'percent'
            ? percentOf(discountableUnits, toBasisPoints(discountValue))
            : Math.min(toScaled(discountValue, scale), discountableUnits),
    );

    const discountUnits = Math.min(rowDiscountUnits + headerDiscountUnits, grossUnits);

    // Gross less both discounts, still excluding sales tax. The tax-inclusive
    // net the customer settles is this plus the per-line tax accumulated below,
    // so the two are deliberately different figures.
    const valueExUnits = Math.max(grossUnits - discountUnits, 0);

    // Push the header discount down onto the lines, largest-remainder, so the
    // per-line values of supply sum exactly to the sale's taxable amount.
    const headerShareUnits = Math.max(discountUnits - rowDiscountUnits, 0);
    const discountWeights = {};
    lines.forEach((line, index) => {
        discountWeights[index] = Math.max(line.grossUnits - line.rowDiscountUnits, 0);
    });
    // In whole rupees the shares are whole too: the rupees are apportioned and
    // scaled back, so no line carries a fraction of the discount.
    const headerDiscountByLine = !lines.length
        ? {}
        : roundTotal
          ? Object.fromEntries(
                Object.entries(apportionCents(Math.trunc(headerShareUnits / scale), discountWeights)).map(([key, rupees]) => [key, rupees * scale]),
            )
          : apportionCents(headerShareUnits, discountWeights);

    let embeddedTaxUnitsTotal = 0;
    let taxBasisUnitsTotal = 0;
    // Value of supply plus the tax added to it, a line at a time. No longer
    // gross - discount, because gross and both discounts are ex-tax now.
    let taxableUnits = 0;
    // One base per header component: each reaches a different set of lines, so
    // a single blended figure charged further tax on exempt goods and FED on
    // goods that are not excisable at all. Mirrors SaleFinancialCalculator.
    let furtherTaxBaseUnits = 0;
    let fedTaxBaseUnits = 0;
    let extraTaxBaseUnits = 0;
    const lineBreakdown = lines.map((line, index) => {
        const apportionedDiscount = headerDiscountByLine[index] ?? 0;
        const lineDiscount = line.rowDiscountUnits + apportionedDiscount;
        // Value sales excluding sales tax for this line.
        const lineValueEx = Math.max(line.grossUnits - lineDiscount, 0);
        // Third-schedule goods are taxed on printed retail price, so the basis
        // ignores the discount. No MRP falls back to the line's own value. MRP
        // is a retail price and therefore tax-inclusive, so its own tax comes
        // out before it can stand as the §2(46) basis.
        const taxBasis =
            line.isThirdSchedule && line.mrpUnits > 0
                ? whole(exclusiveOfTaxCents(extendedUnits(line.mrpUnits, line.quantityUnits), line.taxBps))
                : lineValueEx;
        // Added on top, not carved out — the rate on screen is the rate the tax
        // stands on.
        // Settled to the rupee in turn when the document rounds, so the line's
        // value of supply, its tax and its total are all whole.
        const lineTax = whole(percentOf(taxBasis, line.taxBps));
        const lineTotal = lineValueEx + lineTax;
        embeddedTaxUnitsTotal += lineTax;
        taxBasisUnitsTotal += taxBasis;
        taxableUnits += lineTotal;

        const lineValueOfSupply = taxBasis;
        // Further tax (s.3(1A)) and extra tax (s.3(5)) are charges inside the
        // sales tax regime and reach taxable supplies only; further tax also
        // skips third-schedule goods, already taxed upstream on retail price.
        // FED reaches First Schedule goods only.
        const isTaxableSupply = line.supplyType === 'standard';
        const furtherTaxable = isTaxableSupply && !line.isThirdSchedule;
        if (furtherTaxable) furtherTaxBaseUnits += lineValueOfSupply;
        if (isTaxableSupply) extraTaxBaseUnits += lineValueOfSupply;
        if (line.fedExcisable) fedTaxBaseUnits += lineValueOfSupply;

        return {
            // The cart row this line came from, so the POS can show each line
            // the tax that was actually charged on it.
            itemIndex: line.itemIndex,
            grossAmount: up(line.grossUnits),
            // Split out because the cart shows them separately: what the cashier
            // typed on the row, and the row's share of the invoice discount.
            rowDiscountAmount: up(line.rowDiscountUnits),
            apportionedDiscountAmount: up(apportionedDiscount),
            discountAmount: up(lineDiscount),
            valueExAmount: up(lineValueEx),
            // What the customer pays for this line — the Amount column.
            taxableAmount: up(lineTotal),
            taxBasisAmount: up(taxBasis),
            embeddedTaxAmount: up(lineTax),
            // §2(46) value excluding sales tax for this line.
            valueOfSupply: up(lineValueOfSupply),
            supplyType: line.supplyType,
            furtherTaxable,
            extraTaxable: isTaxableSupply,
            fedExcisable: line.fedExcisable,
        };
    });

    // Sales Tax Act 1990 s.2(46) — the value of supply excludes the amount of
    // sales tax, so further tax, FED and extra tax stand on this and not on the
    // tax-inclusive figure. The line chain is ex-tax throughout, so the
    // accumulated basis already *is* that value. Mirrors SaleFinancialCalculator
    // exactly; if the two disagree by more than a paisa the server rejects the
    // sale as tampered.
    const valueOfSupplyUnits = taxBasisUnitsTotal;

    const salesTaxPercentage = Math.min(normalizeNonNegativeNumber(form.salesTax), 100);
    const fedTaxPercentage = Math.min(normalizeNonNegativeNumber(form.fed_tax), 100);
    const extraTaxPercentage = Math.min(normalizeNonNegativeNumber(form.extra_tax), 100);

    // Each on its own base rather than on the blended value of supply above:
    // an exempt line still has a value, it just cannot bear these charges.
    // `salesTax` is the further-tax rate (historic field name, matching
    // sales.sales_tax_percentage).
    // Each header figure settles to the rupee in turn when the document
    // rounds — the taxes as they are charged, the delivery as typed.
    const taxUnits = whole(percentOf(furtherTaxBaseUnits, toBasisPoints(salesTaxPercentage)));
    const fedTaxUnits = whole(percentOf(fedTaxBaseUnits, toBasisPoints(fedTaxPercentage)));
    const extraTaxUnits = whole(percentOf(extraTaxBaseUnits, toBasisPoints(extraTaxPercentage)));
    const deliveryUnits = whole(toScaled(form.delivery_charges ?? 0, scale));

    // Advance income tax (236G/236H) is collected on the gross amount of the
    // sale — the whole invoice value including sales tax and delivery. It is
    // withheld for the buyer, so it is neither revenue nor output tax.
    const invoiceValueUnits = taxableUnits + taxUnits + fedTaxUnits + extraTaxUnits + deliveryUnits;
    const advanceTaxRate = Math.min(normalizeNonNegativeNumber(form.advance_tax_rate), 100);
    const advanceTaxUnits = whole(percentOf(invoiceValueUnits, toBasisPoints(advanceTaxRate)));

    // The payable: the exact total settled to the paisa once, as the server
    // settles it — already paisa at two places, rounded once at four — and
    // then to the whole rupee when the installation rounds invoice totals. A
    // whole-rupee chain is already whole, so that last step is a guarantee
    // rather than a change; what it moves, if anything, is the Rounding row.
    const exactTotalUnits = invoiceValueUnits + advanceTaxUnits;
    const finalTotalCents = roundTotal ? roundCentsToUnit(centsFromScaled(exactTotalUnits, scale)) : centsFromScaled(exactTotalUnits, scale);

    return {
        grossAmount: up(grossUnits),
        rowDiscountAmount: up(rowDiscountUnits),
        headerDiscountAmount: up(headerDiscountUnits),
        discountValue,
        discountAmount: up(discountUnits),
        // Gross less both discounts, before sales tax is added.
        valueExAmount: up(valueExUnits),
        taxableAmount: up(taxableUnits),
        netAmount: up(taxableUnits),
        embeddedTaxAmount: up(embeddedTaxUnitsTotal),
        taxBasisAmount: up(taxBasisUnitsTotal),
        valueOfSupply: up(valueOfSupplyUnits),
        // Narrower than valueOfSupply whenever the basket holds an exempt,
        // zero-rated, third-schedule or non-excisable line. Shown on the
        // checkout summary so the base each rate stands on is on screen.
        furtherTaxBase: up(furtherTaxBaseUnits),
        fedTaxBase: up(fedTaxBaseUnits),
        extraTaxBase: up(extraTaxBaseUnits),
        lines: lineBreakdown,
        salesTaxPercentage,
        taxAmount: up(taxUnits),
        fedTaxPercentage,
        fedTaxAmount: up(fedTaxUnits),
        extraTaxPercentage,
        extraTaxAmount: up(extraTaxUnits),
        deliveryCharges: up(deliveryUnits),
        invoiceValue: up(invoiceValueUnits),
        advanceTaxSection: form.advance_tax_section ?? null,
        advanceTaxRate,
        advanceTaxBase: up(invoiceValueUnits),
        advanceTaxAmount: up(advanceTaxUnits),
        // In paisa: what the customer is asked for.
        finalTotal: fromCents(finalTotalCents),
        // The payable less the exact total: zero at two places, under half a
        // paisa either way at four — and up to half a rupee either way when
        // the total was rounded. The Rounding row.
        rounding: fromRateUnits(roundingUnitsFromScaled(finalTotalCents, exactTotalUnits, scale)),
        roundTotal,
        decimals,
    };
};

// ── Exchange rates (mirrors MoneyMath's exchange-rate section) ────────────
//
// A rate is base units per one unit of another currency ("1 USD = 280.50 PKR"
// is 280.5), held to ten places. Conversion is exact integer arithmetic on
// BigInt — a float product of a paisa amount and a ten-place rate loses the
// last paisa on large figures — rounded once, half away from zero, exactly as
// MoneyMath::convertCents rounds. Every helper works on integer minor units at
// any scale. See docs/multi-currency-plan.md.

export const EXCHANGE_RATE_DECIMALS = 10;
const RATE_FACTOR = 10n ** BigInt(EXCHANGE_RATE_DECIMALS);

/** A rate as an integer of ten-billionths (BigInt); 0n when it is not a positive number. */
export const rateUnits = (rate) => {
    const raw = String(rate ?? '').trim();
    if (!/^\d+(\.\d+)?$/.test(raw)) return 0n;
    const [whole, fraction = ''] = raw.split('.');
    const padded = (fraction + '0'.repeat(EXCHANGE_RATE_DECIMALS + 1)).slice(0, EXCHANGE_RATE_DECIMALS + 1);
    let units = BigInt(whole) * RATE_FACTOR + BigInt(padded.slice(0, EXCHANGE_RATE_DECIMALS));
    if (Number(padded[EXCHANGE_RATE_DECIMALS]) >= 5) units += 1n;
    return units;
};

/** A positive rate normalised to the decimal string the server stores, or null. */
export const normalizeExchangeRate = (rate) => {
    const units = rateUnits(rate);
    if (units <= 0n) return null;
    const whole = units / RATE_FACTOR;
    const fraction = String(units % RATE_FACTOR)
        .padStart(EXCHANGE_RATE_DECIMALS, '0')
        .replace(/0+$/, '');
    return fraction ? `${whole}.${fraction}` : `${whole}`;
};

/** numerator / denominator, half away from zero (BigInt in, Number out). */
const divideHalfUp = (numerator, denominator) => {
    if (denominator === 0n) return 0;
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    let q = n / d;
    if ((n % d) * 2n >= d) q += 1n;
    return Number(negative ? -q : q);
};

/** Minor units of a currency → minor units of the base. Mirrors MoneyMath::convertCents. */
export const convertCents = (units, rate) => {
    const r = rateUnits(rate);
    if (!units || r === 0n) return 0;
    return divideHalfUp(BigInt(Math.trunc(units)) * r, RATE_FACTOR);
};

/** Minor units of the base → the currency the rate quotes. Mirrors MoneyMath::convertCentsBack. */
export const convertCentsBack = (baseUnits, rate) => {
    const r = rateUnits(rate);
    if (!baseUnits || r === 0n) return 0;
    return divideHalfUp(BigInt(Math.trunc(baseUnits)) * RATE_FACTOR, r);
};

/** One currency into another through the base, one rounding. Mirrors MoneyMath::crossCents. */
export const crossCents = (units, fromRate, toRate) => {
    const from = rateUnits(fromRate);
    const to = rateUnits(toRate);
    if (!units || from === 0n || to === 0n) return 0;
    return divideHalfUp(BigInt(Math.trunc(units)) * from, to);
};

/** A major-unit amount converted at a rate, settled to the paisa. */
export const convertAmount = (amount, rate) => {
    const number = Number(amount) || 0;
    const cents = Math.round(Math.abs(number) * 100);
    const converted = convertCents(cents, rate);
    return (number < 0 ? -converted : converted) / 100;
};

/**
 * What a tender is worth to a document, mirroring Currency::applyToDocument:
 * the same currency applies exactly; anything else converts through the base
 * at the tender's rate and the document currency's rate on the day.
 * `null` currency means the base. Returns cents of the document's currency.
 */
export const appliedToDocumentCents = (tenderCents, tenderCurrency, tenderRate, documentCurrency, documentRate) => {
    if ((tenderCurrency || null) === (documentCurrency || null)) return Math.trunc(tenderCents);
    const from = tenderCurrency ? tenderRate : '1';
    const to = documentCurrency ? documentRate : '1';
    return crossCents(tenderCents, from, to);
};

/** The inverse: what a tender must be to cover document cents. Mirrors Currency::documentToTenderCents. */
export const documentToTenderCents = (documentCents, documentCurrency, documentRate, tenderCurrency, tenderRate) => {
    if ((tenderCurrency || null) === (documentCurrency || null)) return Math.trunc(documentCents);
    const from = documentCurrency ? documentRate : '1';
    const to = tenderCurrency ? tenderRate : '1';
    return crossCents(documentCents, from, to);
};

// ── Split tender (mirrors Currency::checkoutTender / checkoutTenders) ─────
//
// Several payments taken at one checkout, applied in the server's fixed order —
// a wallet leg, then card / QR / account legs (by the machine or account they
// land on), cash last, the only leg that gives change — each against what is
// still owed. Pinned to the PHP by tests/Fixtures/split-tender-mirror.json.
// See docs/pos-split-tender-plan.md.

const moneyLabel = (cents) => (Math.round(Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

class SplitTenderError extends Error {
    constructor(message, key = null) {
        super(message);
        this.key = key;
    }
}

/**
 * One tender against an amount owed (document cents), mirroring
 * Currency::checkoutTender. `input` carries cash_received_amount /
 * tender_amount and the cash change split. Throws in the server's words.
 */
export const checkoutTenderCents = (method, finalCents, documentCurrency, bookingRate, tenderCurrency, tenderRate, input = {}) => {
    if (!['cash', 'online', 'card', 'wallet'].includes(method)) throw new SplitTenderError('Invalid payment method.');
    const doc = documentCurrency || null;
    const tc = tenderCurrency || null;
    const booking = doc === null ? '1' : normalizeExchangeRate(bookingRate);
    const rate = tc === null ? '1' : normalizeExchangeRate(tenderRate);
    const same = doc === tc;
    const finalInTender = documentToTenderCents(finalCents, doc, booking, tc, rate);
    const tolerance = same ? 0 : Math.max(1, crossCents(1, rate, booking) + 1);
    const toApplied = (cents) => (same ? cents : crossCents(cents, rate, booking));

    let paidCents;
    let tenderCents;
    let receivedCents = 0;
    let cashChangeCents = 0;
    let walletChangeCents = 0;
    let branchCashCents = 0;

    if (method === 'cash') {
        receivedCents = toCents(input.cash_received_amount ?? input.tender_amount ?? 0);
        let change;
        if (receivedCents >= finalInTender) {
            paidCents = finalCents;
            change = receivedCents - finalInTender;
        } else {
            paidCents = Math.min(finalCents, toApplied(receivedCents));
            change = 0;
        }
        walletChangeCents = input.credit_change_to_wallet ? toCents(input.change_amount ?? 0) : 0;
        const changeProvided = 'cash_change_amount' in input || 'change_amount' in input || Boolean(input.credit_change_to_wallet);
        cashChangeCents = changeProvided ? toCents(input.cash_change_amount ?? 0) : change - walletChangeCents;
        if (Math.abs(cashChangeCents + walletChangeCents - change) > 1) {
            throw new SplitTenderError(`Cash change plus wallet change must equal the change due (${fromCents(change)}).`);
        }
        branchCashCents = receivedCents - cashChangeCents;
        tenderCents = receivedCents - cashChangeCents - walletChangeCents;
    } else {
        tenderCents = toCents(input.tender_amount ?? 0);
        if (method === 'wallet' && (input.tender_amount ?? null) === null) tenderCents = finalInTender;
        const applied = toApplied(tenderCents);
        if (applied > finalCents + tolerance) throw new SplitTenderError('Paid amount cannot exceed the final total.');
        paidCents = Math.min(applied, finalCents);
    }

    return {
        paidCents,
        tenderCents,
        receivedCents,
        cashChangeCents,
        walletChangeCents,
        branchCashCents,
        tolerance,
        tender: {
            currencyCode: tc,
            currencyAmountCents: tenderCents,
            exchangeRate: rate,
            baseCents: convertCents(tenderCents, rate),
            appliedRate: same ? rate : booking,
        },
    };
};

/**
 * Several tenders at one checkout, mirroring Currency::checkoutTenders.
 * A leg is `{ key, method, amount, currency (null = base), rate, order }`.
 * Never throws: a refusal comes back as `error: { message, key }`, the
 * server's sentence and the leg it is about, so the payment modal can show it
 * against that row before anything is sent.
 */
export const splitTender = (legs = [], { finalCents = 0, documentCurrency = null, bookingRate = '1', baseCode = 'PKR', change = {} } = {}) => {
    const result = {
        paidCents: 0,
        remainingCents: Math.max(0, Math.trunc(finalCents)),
        receivedCents: 0,
        cashChangeCents: 0,
        walletChangeCents: 0,
        branchCashCents: 0,
        tolerance: 0,
        legs: [],
        error: null,
    };

    try {
        const doc = documentCurrency || null;
        const booking = doc === null ? '1' : normalizeExchangeRate(bookingRate);
        const counts = legs.reduce((acc, leg) => ({ ...acc, [leg.method]: (acc[leg.method] ?? 0) + 1 }), {});
        if ((counts.cash ?? 0) > 1) throw new SplitTenderError('Take cash as one payment: add the cash amounts together.');
        if ((counts.wallet ?? 0) > 1) throw new SplitTenderError('Take the wallet as one payment.');

        const rank = (method) => (method === 'wallet' ? 0 : method === 'cash' ? 2 : 1);
        const ordered = [...legs].sort(
            (a, b) =>
                rank(a.method) - rank(b.method) || (Number(a.order) || 0) - (Number(b.order) || 0) || (Number(a.key) || 0) - (Number(b.key) || 0),
        );

        let remaining = Math.trunc(finalCents);
        for (const leg of ordered) {
            const method = String(leg.method);
            const label = method;
            const currency = leg.currency || null;
            const rate = currency === null ? '1' : normalizeExchangeRate(leg.rate ?? 1);

            if (remaining <= 0) {
                throw new SplitTenderError(`Nothing is left to pay for the ${label} payment. Remove it, or lower one of the others.`, leg.key);
            }

            let input;
            if (method === 'cash') {
                input = { cash_received_amount: leg.amount };
                for (const field of ['cash_change_amount', 'change_amount', 'credit_change_to_wallet']) {
                    if (field in change) input[field] = change[field];
                }
            } else {
                const tenderCents = toCents(leg.amount);
                const same = currency === doc;
                const appliedCents = same ? tenderCents : crossCents(tenderCents, rate, booking);
                const legTolerance = same ? 0 : Math.max(1, crossCents(1, rate, booking) + 1);
                if (appliedCents > remaining + legTolerance) {
                    const left = documentToTenderCents(remaining, doc, booking, currency, rate);
                    throw new SplitTenderError(
                        `The ${label} payment is more than what is left to pay (${moneyLabel(left)} ${currency ?? baseCode}).`,
                        leg.key,
                    );
                }
                input = { tender_amount: leg.amount };
            }

            let checkout;
            try {
                checkout = checkoutTenderCents(method, remaining, doc, booking, currency, rate, input);
            } catch (error) {
                throw new SplitTenderError(error.message, leg.key);
            }
            if (checkout.paidCents <= 0 && method !== 'cash') {
                throw new SplitTenderError(`The ${label} payment is too small to count towards this sale.`, leg.key);
            }

            remaining -= checkout.paidCents;
            result.paidCents += checkout.paidCents;
            result.tolerance += checkout.tolerance;
            if (method === 'cash') {
                result.receivedCents = checkout.receivedCents;
                result.cashChangeCents = checkout.cashChangeCents;
                result.walletChangeCents = checkout.walletChangeCents;
                result.branchCashCents = checkout.branchCashCents;
            }
            result.legs.push({ key: leg.key, method, ...checkout });
        }
        result.remainingCents = Math.max(0, remaining);
    } catch (error) {
        result.error = { message: error.message, key: error.key ?? null };
    }

    return result;
};
